<template>
  <div
    id="bio-material-entity-form"
    v-if="action"
    data-test="bio-material-form"
  >
    <h2 v-if="!isInModal" id="bio-material-type-title">
      {{ pageTitle }}
    </h2>
    <details-field-group
      :id="uuid"
      label="Library Details"
      :is-bio-styling="true"
      :collapsed="true"
      v-if="canShowEntityDetails()"
    >
      <BioMaterialView
        :material-type="materialType"
        :uuid="uuid"
        :hide-title="true"
        :group-nid="groupNid"
        :omit-fields="editFields"
        :show-add-to-library="false"
        :bio-material-data="response"
        :show-library-style="true"
      />
    </details-field-group>
    <details-field-group
      id="edit-material"
      :label="modificationsTitles"
      :is-bio-styling="true"
      :collapsed="false"
    >
      <b-form @submit.prevent="submitEntity">
        <div v-for="fieldData in bundleFieldsSorted">
          <raft-autocomplete
            v-if="isAutocompleteField(fieldData)"
            field-class="entity-reference-field"
            ref="autocomplete"
            :id="fieldData.field_name"
            :name="fieldData.field_name"
            :value="selectedPlasmidVector"
            :label="fieldData.label"
            element-id="plasmid-vector-autocomplete"
            placeholder="Add Plasmid/Vector Type"
            :source="plasmidVectorLookup"
            :results-display="formattedResultsDisplay"
            input-class="form-control"
            @selected="updateSelectedPlasmidVector"
            :default-value="autocompleteDefaultValue(fieldData)"
            :key="fieldData.field_name + 'autocomplete'"
            aria-label="Plasmid/vector selector"
          >
            <template #action>
              <div class="copy-plasmid-vector-btn">
                <b-button
                  v-if="showCopyPlasmidVectorButton"
                  @click="copyPlasmidVectorModifications"
                >
                  Copy Genetic Modifications
                </b-button>
              </div>
            </template>
          </raft-autocomplete>
          <entity-form-field
            v-else-if="showField(fieldData)"
            :field-data="fieldData"
            :entity="entity"
            :parent-value="formData[fieldData.field_name]"
            entity-type="bio_material"
            :bundle="materialType"
            :placeholder="getPlaceholder(fieldData.field_name)"
            @disable-form-submit="submitDisabled = true"
            @form-error-message="catchFormError"
            @update-form-data="updateFormData"
            :key="fieldData.field_name"
            :no-default-value="isUuidPresent"
          />
        </div>
        <div class="bio-material-action-buttons">
          <div class="buttons-left">
            <v-button
              v-if="showRemoveFromGroupButton && isInModal"
              class="clear small"
              id="modal-remove-from-group"
              @click="removeMaterialInModal"
            >
              <img class="remove-icon" alt="trash" :src="trashSrc" />
              Remove from {{ inGroup ? "Group" : "Library" }}
            </v-button>

            <v-button
              v-if="showRemoveFromGroupButton && !isInModal"
              class="clear small"
              id="modal-remove-from-group"
            >
              <img class="remove-icon" alt="trash" :src="trashSrc" />
              <b-link :href="removeLink">
                Remove from {{ inGroup ? "Group" : "Library" }}
              </b-link>
            </v-button>
          </div>

          <div class="buttons-right">
            <v-button
              v-if="showRemoveFromGroupButton && isInModal"
              class="clear small"
              id="modal-add-without-changes"
              @click="addWithoutChanges"
              >Add Without Changes
            </v-button>
            <v-button v-if="!isInModal" class="clear small">
              <b-link class="cancel" href="/" @click.prevent="cancel">
                {{ cancelText }}
              </b-link>
            </v-button>
            <v-button type="submit" :class="disableButton"> Apply </v-button>
          </div>
        </div>
      </b-form>
    </details-field-group>
  </div>
</template>

<script>
import DetailsFieldGroup from "../raft/DetailsFieldGroup.vue";
import BioMaterialView from "./BioMaterialView.vue";
import EntityFormField from "../EntityFormField.vue";
import EntityFormHelper from "../../lib/EntityFormHelper.js";
import RaftAutocomplete from "../raft/RaftAutocomplete.vue";
import { restClient, jsonClient } from "../../lib/raftApi";
import trash from "../../images/trash.svg";
import VButton from "../raft/VButton.vue";

export default {
  name: "BioMaterialForm",
  props: {
    materialType: {
      type: String,
      required: true,
    },
    uuid: {
      type: String,
      required: true,
    },
    groupNid: {
      type: String,
      default: null,
    },
    isInModal: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      trashSrc: trash,
      conditionalFields: {
        genetic_modification: {
          field: "genetically_modified",
          showValues: ["generated", "acquired"],
        },
        research_genetic_modification: {
          field: "genetically_modified",
          showValues: ["generated", "acquired"],
        },
        gene_mod_category: {
          field: "genetically_modified",
          showValues: ["generated", "acquired"],
        },
        research_gene_mod_category: {
          field: "genetically_modified",
          showValues: ["generated", "acquired"],
        },
        field_biomat_plasmid_vector_type: {
          field: "genetically_modified",
          showValues: ["generated", "acquired"],
        },
        field_identify_animal_use: {
          field: "field_agent_used_in_animals",
          showValues: [true],
        },
        field_source_url: {
          field: "field_tissue_source",
          showValues: ["commercial_supplier"],
        },
      },
      fields: {},
      response: null,
      defaultValuesReady: false,
      formData: {},
      changedData: {},
      submitDisabled: true,
      entity: { id: "", type: "" },
      materialTypeLabel: "",
      ogAudience: "",
      editFields: [],
      action: "",
      inGroup: false,
      groupEntity: {},
      selectedPlasmidVector: null,
      showCopyPlasmidVectorButton: false,
      query: {},
    };
  },
  methods: {
    isAutocompleteField(fieldData) {
      return (
        this.showField(fieldData) &&
        EntityFormHelper.getInputType(fieldData.field_type, fieldData) ===
          "autocomplete"
      );
    },
    autocompleteDefaultValue(fieldData) {
      if (fieldData.field_name === "field_biomat_plasmid_vector_type") {
        return this.selectedPlasmidVector;
      }
      return null;
    },
    copyPlasmidVectorModifications() {
      // Resets Insert Categories and Genetic Modifications/Inserts
      this.$set(this.changedData, "research_genetic_modification", "");
      this.$set(this.formData, "research_genetic_modification", "");
      // Replace values from anything already selected with the selected plasmid/vector
      // genetic modification categories.
      let fieldName = "gene_mod_category";
      if (this.inGroup) {
        fieldName = "research_gene_mod_category";
      }
      let vectorFieldName = "research_gene_mod_category";
      if (this.selectedPlasmidVector?.in_library) {
        vectorFieldName = "gene_mod_category";
      }
      let updated = {
        field_name: fieldName,
        field_value: this.selectedPlasmidVector?.[vectorFieldName],
      };
      this.updateFormData(updated);

      fieldName = "genetic_modification";
      if (this.inGroup) {
        fieldName = "research_genetic_modification";
      }
      vectorFieldName = "research_genetic_modification";
      if (this.selectedPlasmidVector?.in_library) {
        vectorFieldName = "genetic_modification";
      }
      // Replace any genetic modification/inserts with values from the selected plasmid/vector,
      // but only if the vector field isn't empty.
      if (
        this.selectedPlasmidVector?.[vectorFieldName] &&
        this.selectedPlasmidVector?.[vectorFieldName].toString() !== ""
      ) {
        updated = {
          field_name: fieldName,
          field_value: this.selectedPlasmidVector?.[vectorFieldName],
        };
        this.updateFormData(updated);
      }
      // Override the Library Risk/Containment Level field value if appropriate.
      fieldName = "containment_bsl";
      if (this.inGroup) {
        fieldName = "research_containment_bsl";
      }
      vectorFieldName = "research_containment_bsl";
      if (this.selectedPlasmidVector?.in_library) {
        vectorFieldName = "containment_bsl";
      }
      // Make sure the values are strings for comparison. This correctly treats
      // "2-3" as > "2" and < "3".
      const vectorValue = String(this.selectedPlasmidVector?.[vectorFieldName]);
      const currentValue = String(this.formData?.[fieldName]);
      // Only update the value if the vector BSL value is greater.
      if (currentValue < vectorValue) {
        updated = {
          field_name: fieldName,
          field_value: vectorValue,
        };
        this.updateFormData(updated);
      }
    },
    async plasmidVectorLookup(input, callback) {
      const queries = {
        "filter[status]": 1,
        "filter[operative_status]": 1,
        "filter[workflow_status]": 1,
        "filter[name][condition][path]": "name",
        "filter[name][condition][value]": input,
        "filter[name][condition][operator]": "CONTAINS",
      };
      if (this.groupNid) {
        // Needed for access.
        queries["rog-type"] = "node";
        queries["rog-id"] = this.groupNid;
      }
      if (this.inGroup) {
        queries["filter[library-group][group][conjunction]"] = "OR";
        queries["filter[library][condition][path]"] = "in_library";
        queries["filter[library][condition][value]"] = 1;
        queries["filter[library][condition][memberOf]"] = "library-group";
        // This is ugly, but allows for direct filtering instead of trying to
        // filter through the node entity.
        queries["filter[rog][condition][path]"] =
          "og_audience.meta.drupal_internal__target_id";
        queries["filter[rog][condition][value]"] =
          this.groupEntity?.attributes?.drupal_internal__nid;
        queries["filter[rog][condition][memberOf]"] = "library-group";
      } else {
        queries["filter[library][condition][path]"] = "in_library";
        queries["filter[library][condition][value]"] = 1;
      }
      const plasmidTypeResponseData = await jsonClient
        .get({ path: "/bio_material/plasmid", queries })
        .catch((e) =>
          this.errorEmit(`Failed to find any plasmid/vector types. ${e}`),
        );
      const results = plasmidTypeResponseData?.deserialized;
      return callback(results);
    },
    formattedResultsDisplay(result) {
      // Add identifier materials in a group.
      let displayName = result.field_name;
      if (!result.in_library) {
        displayName += " [in group]";
      }
      return displayName;
    },
    showField(fieldData) {
      // A bio material's associated group cannot change once it's created.
      if (fieldData.field_name === "og_audience") {
        return false;
      }
      const fieldName = fieldData.field_name;
      if (this.conditionalFields[fieldName]) {
        let showValues = [];
        if (this.conditionalFields[fieldName].showValues) {
          showValues = this.conditionalFields[fieldName].showValues;
        }
        const parentField = this.conditionalFields[fieldName].field;
        // Handle the case of missing parent field like GMO on plasmid / vector.
        if (!this.formData.hasOwnProperty(parentField)) {
          return true;
        }
        if (Array.isArray(this.formData[parentField])) {
          return (
            showValues.filter((x) => this.formData[parentField].includes(x))
              .length > 0
          );
        }
        if (typeof this.formData[parentField] === "boolean") {
          return this.formData[parentField];
        }
        // Initial JSON:API data for e.g. genetically_modified field is integer.
        const value = String(this.formData[parentField]);
        return showValues.includes(value);
      }
      return true;
    },
    updateSelectedPlasmidVector(value) {
      const currentPlasmid =
        this.entity.relationships.field_biomat_plasmid_vector_type;
      // If the current entity doesn't have a selected plasma, or if it does
      // and it's different than the one
      // selected in the autocomplete, update the form values.
      if (
        !currentPlasmid.data ||
        (currentPlasmid.data &&
          (!currentPlasmid.data.length ||
            value.selectedObject.id !== currentPlasmid.data[0].id))
      ) {
        this.showCopyPlasmidVectorButton = true;
        const selectedValue = {
          field_name: "field_biomat_plasmid_vector_type",
          field_value: {
            type: value.selectedObject.type,
            id: value.selectedObject.id,
          },
        };
        this.selectedPlasmidVector = value.selectedObject;
        this.updateFormData(selectedValue);
      }
    },
    updateFormData(value) {
      this.$set(this.formData, value.field_name, value.field_value);
      this.$set(this.changedData, value.field_name, value.field_value);
      this.submitDisabled = false;
    },
    catchFormError(error) {
      this.$emit("form-error-message", error);
    },
    errorEmit(message, data) {
      this.$emit("form-error-message", { message: message, data: data });
    },
    async submitEntity() {
      this.submitDisabled = true;
      const data = EntityFormHelper.createJsonApiEntityData(
        "bio_material",
        this.materialType,
      );

      EntityFormHelper.fillJsonApiDataFromFormData(
        data,
        this.bundleFieldsSorted,
        this.changedData,
      );

      data.id = this.entity.id;
      // Make sure the workflow status is set to published.
      data.attributes.workflow_status = 1;

      const queries = {};
      if (this.groupNid) {
        queries["rog-id"] = this.groupNid;
        queries["rog-type"] = "node";
      }
      const path = "bio_material/" + this.materialType + "/" + this.uuid;
      const patchResponseData = await jsonClient
        .patch({
          path,
          queries,
          data,
        })
        .catch((e) => {
          if (this.isInModal) {
            this.$showNotification(
              "error",
              "Unable to modify material. Please try again.",
            );
          }
          this.errorEmit(e);
        });
      // Allow the parent element to handle anything needed.
      if (patchResponseData) {
        this.$emit("submit-form-data", "", patchResponseData);
        // If within modal, proceed to next step
        if (this.isInModal) {
          this.$showNotification(
            "success",
            "Material has been successfully modified.",
          );
          this.$emit("choose-modification", true);
          this.emitStepChange();
        }
        this.submitDisabled = false;
      }
    },
    cancel(event) {
      event.preventDefault();
      let toastText = "";
      if (this.cancelText === "Skip") {
        toastText = this.cancelText + "ping";
      } else {
        toastText = this.cancelText + "ing";
      }
      this.$emit("submit-form-data", toastText);
    },
    setDefaultValues() {
      Object.values(this.bundleFieldsSorted).forEach((fieldData) => {
        this.editFields.push(fieldData.field_name);
        this.$set(
          this.formData,
          fieldData.field_name,
          EntityFormHelper.getDefaultValueFromEntity(
            fieldData.field_name,
            fieldData.field_type,
            this.entity,
          ),
        );
      });
      this.ogAudience = this.formData.og_audience;
      this.defaultValuesReady = true;
    },
    getPlaceholder: function (fieldName) {
      if (
        fieldName &&
        typeof this.fields[fieldName]?.form_displays?.default?.settings
          ?.placeholder !== "undefined"
      ) {
        return this.fields[fieldName].form_displays.default.settings
          .placeholder;
      }
      return "";
    },
    canShowEntityDetails: function () {
      return (
        this.entity &&
        !this.entity.attributes?.in_library &&
        this.entity.relationships &&
        this.entity.relationships.parent_reference.data !== null
      );
    },
    emitStepChange() {
      this.$emit("emit-step-change", 4);
    },
    addWithoutChanges() {
      this.$emit("choose-modification", false);
      this.$showNotification(
        "info",
        "Material has been added without modifying.",
      );
      this.emitStepChange();
    },
    removeMaterialInModal() {
      this.$emit("choose-modification", false);
      this.$emit("remove-material");
    },
    emitCloseModal() {
      this.$emit("emit-close-modal");
    },
  },
  computed: {
    bundleFieldsSorted() {
      const bundleFieldsSorted = [];
      if (this.fields) {
        Object.values(this.fields).forEach((fieldData) => {
          if (fieldData?.form_displays?.default) {
            bundleFieldsSorted.push(fieldData);
          }
        });
        bundleFieldsSorted.sort(
          (a, b) =>
            a.form_displays.default.weight - b.form_displays.default.weight,
        );
      }
      return bundleFieldsSorted;
    },
    computedSelectedPlasmidVector() {
      return this.selectedPlasmidVector;
    },
    computedFormData() {
      return this.formData;
    },
    computedChangedData() {
      return this.changedData;
    },
    isUuidPresent() {
      return this.uuid.length > 0;
    },
    inLibrary() {
      return !!this.entity?.attributes?.in_library;
    },
    pageTitle: function () {
      let inTitle = " in Library";
      if (this.inGroup) {
        inTitle = " in " + this.groupTitle;
      }
      return this.action + " " + this.materialTypeLabel + inTitle;
    },
    groupTitle: function () {
      if (this.inGroup && this.groupEntity?.attributes) {
        return this.groupEntity.attributes?.title;
      }
      return "";
    },
    cancelText: function () {
      if (!this.entity?.attributes?.workflow_status) {
        return "Discard";
      }
      if (this.$route?.query?.copyFromLibrary) {
        return "Skip";
      }
      return "Cancel";
    },
    removeLink: function () {
      // @see the code at the end of mounted() that sets a default destination.
      const query = this.query;
      // If we remove the material we don't want to be redirected back to the
      // material (i.e. any path with its UUID).
      if (
        (!query.destination || query?.destination.includes(this.uuid)) &&
        this.inGroup &&
        this.groupEntity?.attributes
      ) {
        query.destination = `/node/${this.groupEntity.attributes.drupal_internal__nid}/biologicals`;
      }
      let queryString = "";
      if (Object.keys(query).length > 0) {
        queryString = "?" + new URLSearchParams(query).toString();
      }
      return this.$route.path.replace("/edit", "/remove") + queryString;
    },
    modificationsTitles() {
      if (this.canShowEntityDetails()) {
        return "Modifications";
      }
      if (!this.inLibrary) {
        return "Details";
      }
      return "Library Details";
    },
    disableButton() {
      return this.submitDisabled ? "disabled" : "primary";
    },
    showRemoveFromGroupButton() {
      return (
        (this.inLibrary || this.inGroup) &&
        this.entity.attributes?.operative_status &&
        this.entity.attributes?.workflow_status
      );
    },
    adjustActionButtons() {
      return this.showRemoveFromGroupButton ? "space-between" : "flex-end";
    },
  },
  async mounted() {
    const restPath = "/fields/bio_material/" + this.materialType;
    const restData = await restClient
      .get({
        path: restPath,
        queries: {
          entity_uuid: this.uuid,
          wrap_allowed_values: 1,
        },
      })
      .catch(({ message, data }) => this.errorEmit(message, data));
    this.materialTypeLabel = restData?.["bundle-label"];
    // @todo bad for performance in V8
    delete restData?.["bundle-label"];
    this.fields = restData;
    const queries = {
      include: "og_audience,parent_reference",
      "fields[node--laboratory]": "title,drupal_internal__nid",
      "fields[node--department]": "title,drupal_internal__nid",
      "fields[node--raft_unit]": "title,drupal_internal__nid",
    };
    const hasVectorPlasmidTypeField =
      !!this.fields.field_biomat_plasmid_vector_type;
    if (hasVectorPlasmidTypeField) {
      queries.include += ",field_biomat_plasmid_vector_type";
    }

    if (this.groupNid) {
      queries["rog-id"] = this.groupNid;
      queries["rog-type"] = "node";
    }
    this.response = await jsonClient
      .get({ path: `/bio_material/${this.materialType}/${this.uuid}`, queries })
      .catch((e) => this.errorEmit(e));
    this.entity = this.response?.data;
    // Don't display the value of the prior revision log message.
    this.$set(this.entity.attributes, "revision_log_message", "");
    if (hasVectorPlasmidTypeField) {
      const flattened = this.response?.deserialized;
      this.selectedPlasmidVector =
        flattened.field_biomat_plasmid_vector_type[0];
    }
    if (this.entity?.relationships?.og_audience?.data[0]) {
      const groupId = this.entity.relationships.og_audience.data[0].id;
      this.inGroup = true;
      for (let i = 0; i < this.response.included.length; i++) {
        if (this.response.included[i].id === groupId) {
          this.groupEntity = this.response.included[i];
        }
      }
    }
    this.$nextTick(() => {
      this.setDefaultValues();
    });
    // Setting this.action to non-empty shows the form.
    if (this.entity.attributes?.workflow_status === 0) {
      this.updateFormData({ field_name: "workflow_status", field_value: 1 });
      this.action = "Submit new";
    } else {
      this.action = "Edit";
      // Overwrite the value of the prior revision log message.
      this.$set(this.changedData, "revision_log_message", "");
      this.submitDisabled = false;
    }
    // Set a default destination of the view page if needed.
    this.query = this.$route?.query;
    if (!this.isInModal) {
      const query = this.query;
      if (!query.destination) {
        query.destination = this.$route.path.replace("/edit", "");
        const newurl =
          this.$route.path + "?" + new URLSearchParams(query).toString();
        window.history.replaceState(null, "", newurl);
      }
      if (query.destination?.includes("modal=")) {
        query.destination = this.$route.path.replace(
          "?modal=SearchAddFromLibraryModal",
          "",
        );
        query.destination = `/node/${this.groupEntity.attributes.drupal_internal__nid}/biologicals/bio-tracking`;
        const newurl =
          this.$route.path + "?" + new URLSearchParams(query).toString();
        window.history.replaceState(null, "", newurl);
      }
    }
  },
  components: {
    BioMaterialView,
    EntityFormField,
    RaftAutocomplete,
    DetailsFieldGroup,
    VButton,
  },
};
</script>

<style scoped>
.bio-material-action-buttons {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: v-bind(adjustActionButtons);
}

.remove {
  margin-left: auto;
  line-height: 1.5rem;
  display: flex;
  align-items: center;
}

.remove-icon {
  margin-right: 0.25rem;
  color: red;
}

.remove-label {
  padding-top: 0.25rem;
}

#modal-remove-from-group {
  padding: 0;
  color: var(--gray-900);
}
#modal-remove-from-group:hover {
  text-decoration: underline;
}

#modal-add-without-changes {
  color: var(--blue-900);
  text-decoration: underline;
}

.buttons-right {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
}
</style>
